import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { CustomButton } from "../atoms/CustomButton";
import { useTranslation } from "react-i18next";
import { HiOutlineLockClosed } from "react-icons/hi";
import useAuth from "../../hooks/useAuth";
import { useHydrateMyPermissions } from "../../hooks/roles";
import { usePermissions } from "../../utils/permissions";
import { Cap, Level } from "../../utils/rbacCatalog";
import LoadingPage from "./LoadingPage";

const RequireAuth = ({ children }: { children: JSX.Element }) => {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const permissions = usePermissions(auth.user);

  useHydrateMyPermissions();

  if (auth.isLoading) return <LoadingPage />;

  if (!auth.user.isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const canAccessDashboard =
    permissions.isOwner() ||
    permissions.isRootUser() ||
    (permissions.isRoleGoverned() &&
      permissions.can(Cap.DASHBOARD_ACCESS, Level.VIEW));

  if (!canAccessDashboard) {
    if (
      permissions.isRoleGoverned() &&
      auth.user.role_permissions === undefined
    ) {
      return <LoadingPage />;
    }

    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gray-100 dark:bg-gray-900">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-6 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
            <HiOutlineLockClosed className="w-8 h-8 text-amber-600 dark:text-amber-400" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            {t("roleProtection.accessDenied")}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {t("roleProtection.noDashboardAccess")}
          </p>
          <CustomButton
            variant="primary"
            className="mx-auto"
            onClick={() => navigate("/logout")}
            title={t("roleProtection.logout")}
          />
        </div>
      </div>
    );
  }

  return children;
};

export default RequireAuth;
